import React, { useState, useRef } from 'react';
import {
  Palette, Type, Square, Image, Sparkles, QrCode,
  Upload, Loader2, Wand2, Check
} from 'lucide-react';
import requests from '../../../services/httpServices';

const bannerStyles = [
  { id: 1, name: 'Style 1', colors: { primary: '#6a1fbf', accent: '#00c853', background: '#001233' } },
  { id: 2, name: 'Style 2', colors: { primary: '#001233', accent: '#6a1fbf', background: '#0a1628' } },
  { id: 3, name: 'Style 3', colors: { primary: '#ff6b6b', accent: '#ffd93d', background: '#1a1a2e' } },
  { id: 4, name: 'Style 4', colors: { primary: '#4ecdc4', accent: '#556270', background: '#2d3436' } },
];

const fonts = ['Inter', 'Poppins', 'Roboto', 'Open Sans', 'Montserrat', 'Lato', 'Nunito'];

const frameStyles = [
  { id: 'none', name: 'None' },
  { id: 'rounded', name: 'Rounded' },
  { id: 'circle', name: 'Circle' },
  { id: 'gradient', name: 'Gradient' },
];

const lowerThirdTemplates = [
  { id: 'minimal', name: 'Minimal', description: 'Clean & simple' },
  { id: 'modern', name: 'Modern', description: 'Accent bar left' },
  { id: 'classic', name: 'Classic', description: 'Full gradient bg' },
  { id: 'bold', name: 'Bold', description: 'Large gradient text' },
  { id: 'broadcast', name: 'Broadcast', description: 'News style' },
  { id: 'elegant', name: 'Elegant', description: 'Bracketed title' },
  { id: 'corporate', name: 'Corporate', description: 'Two-tone split' },
  { id: 'creative', name: 'Creative', description: 'Underline accent' },
];

const lowerThirdShapes = [
  { id: 'bubble', label: 'Bubble', borderRadius: '999px' },
  { id: 'classic', label: 'Classic', borderRadius: '12px' },
  { id: 'minimal', label: 'Minimal', borderRadius: '4px' },
  { id: 'block', label: 'Block', borderRadius: '0px' }
];

const lowerThirdAnimations = [
  { id: 'none', label: 'None' },
  { id: 'fade', label: 'Fade' },
  { id: 'slide-left', label: 'Slide Left' },
  { id: 'slide-right', label: 'Slide Right' },
  { id: 'slide-up', label: 'Slide Up' },
  { id: 'bounce', label: 'Bounce' }
];

export default function BrandingPanel({ branding = {}, onChange }) {
  const [activeTab, setActiveTab] = useState('colors');
  const fileInputRef = useRef(null);
  const qrLogoInputRef = useRef(null);

  // State with defaults from branding prop
  const [primaryColor, setPrimaryColor] = useState(branding.primaryColor || '#6a1fbf');
  const [accentColor, setAccentColor] = useState(branding.accentColor || '#00c853');
  const [backgroundColor, setBackgroundColor] = useState(branding.backgroundColor || '#001233');
  const [selectedFont, setSelectedFont] = useState(branding.font || 'Inter');
  const [selectedFrame, setSelectedFrame] = useState(branding.frame || 'rounded');
  const [frameColor, setFrameColor] = useState(branding.frameColor || '#6a1fbf');
  const [frameBorderWidth, setFrameBorderWidth] = useState(branding.frameBorderWidth || 2);
  const [frameShadow, setFrameShadow] = useState(branding.frameShadow ?? true);
  const [gradientColor1, setGradientColor1] = useState(branding.gradientColor1 || '#6a1fbf');
  const [gradientColor2, setGradientColor2] = useState(branding.gradientColor2 || '#00c853');
  const [logoUrl, setLogoUrl] = useState(branding.logoUrl || '');
  const [logoPosition, setLogoPosition] = useState(branding.logoPosition || 'top-right');
  const [showLogo, setShowLogo] = useState(branding.showLogo || false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [lowerThirdTemplate, setLowerThirdTemplate] = useState(branding.lowerThirdTemplate || 'modern');
  const [lowerThirdName, setLowerThirdName] = useState(branding.lowerThirdName || '');
  const [lowerThirdTitle, setLowerThirdTitle] = useState(branding.lowerThirdTitle || '');
  const [showLowerThird, setShowLowerThird] = useState(branding.showLowerThird || false);
  const [lowerThirdBackground, setLowerThirdBackground] = useState(branding.lowerThirdBackground ?? true);
  const [lowerThirdBgColor, setLowerThirdBgColor] = useState(branding.lowerThirdBgColor || '#000000');
  const [lowerThirdTextColor, setLowerThirdTextColor] = useState(branding.lowerThirdTextColor || '#ffffff');
  const [lowerThirdTitleColor, setLowerThirdTitleColor] = useState(branding.lowerThirdTitleColor || '#9ca3af');
  // New lower third options
  const [lowerThirdUnderline, setLowerThirdUnderline] = useState(branding.lowerThirdUnderline || false);
  const [lowerThirdBorder, setLowerThirdBorder] = useState(branding.lowerThirdBorder || false);
  const [lowerThirdBorderColor, setLowerThirdBorderColor] = useState(branding.lowerThirdBorderColor || '#6a1fbf');
  const [lowerThirdShape, setLowerThirdShape] = useState(branding.lowerThirdShape || 'minimal');
  const [lowerThirdAnimation, setLowerThirdAnimation] = useState(branding.lowerThirdAnimation || 'none');
  // QR Code state
  const [qrUrl, setQrUrl] = useState(branding.qrUrl || '');
  const [showQrCode, setShowQrCode] = useState(branding.showQrCode || false);
  const [qrPosition, setQrPosition] = useState(branding.qrPosition || 'bottom-right');
  const [qrColor, setQrColor] = useState(branding.qrColor || '#001233');
  const [qrBgColor, setQrBgColor] = useState(branding.qrBgColor || '#ffffff');
  const [qrSize, setQrSize] = useState(branding.qrSize || 'medium');
  const [qrLogo, setQrLogo] = useState(branding.qrLogo || '');
  const [isUploadingQrLogo, setIsUploadingQrLogo] = useState(false);
  // AI Background state
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedBgUrl, setGeneratedBgUrl] = useState(branding.generatedBgUrl || '');
  const [activeBackground, setActiveBackground] = useState(branding.activeBackground || '');

  const buildBrandingObject = (overrides = {}) => ({
    primaryColor, accentColor, backgroundColor,
    font: selectedFont, frame: selectedFrame,
    frameColor, frameBorderWidth, frameShadow,
    gradientColor1, gradientColor2,
    logoUrl, logoPosition, showLogo,
    lowerThirdTemplate, lowerThirdName, lowerThirdTitle,
    showLowerThird, lowerThirdBackground, lowerThirdBgColor,
    lowerThirdTextColor, lowerThirdTitleColor,
    lowerThirdUnderline, lowerThirdBorder, lowerThirdBorderColor,
    lowerThirdShape, lowerThirdAnimation,
    qrUrl, showQrCode, qrPosition, qrColor, qrBgColor, qrSize, qrLogo,
    activeBackground,
    generatedBgUrl,
    ...overrides
  });

  const handleChange = (key, value) => {
    onChange?.(buildBrandingObject({ [key]: value }));
  };

  // Convert file to base64 data URL (shareable across participants via PubSub)
  const fileToDataUrl = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // File upload handler for logo
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingLogo(true);
    try {
      // Convert to base64 data URL - works across all browsers/participants
      const dataUrl = await fileToDataUrl(file);
      setLogoUrl(dataUrl);
      handleChange('logoUrl', dataUrl);
    } catch (error) {
      console.error('Failed to process logo:', error);
    } finally {
      setIsUploadingLogo(false);
    }
  };

  // File upload handler for QR logo
  const handleQrLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingQrLogo(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      setQrLogo(dataUrl);
      handleChange('qrLogo', dataUrl);
    } catch (error) {
      console.error('Failed to process QR logo:', error);
    } finally {
      setIsUploadingQrLogo(false);
    }
  };

  // AI Background Generator — image generation is local, but activeBackground URL is synced via PubSub
  const generateAiBackground = async () => {
    if (!aiPrompt.trim()) return;
    setIsGenerating(true);
    try {
      const data = await requests.post('/openai/generate-background', {
        prompt: aiPrompt.trim(),
      });
      const url = data?.imageUrl;
      if (url) {
        setGeneratedBgUrl(url);
        handleChange('generatedBgUrl', url);
      }
    } catch (error) {
      console.error('Failed to generate background:', error);
      alert('Failed to generate background. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const tabs = [
    { id: 'colors', label: 'Colors', icon: Palette },
    { id: 'style', label: 'Style', icon: Type },
    { id: 'media', label: 'Media', icon: Image },
    { id: 'qr', label: 'QR', icon: QrCode },
  ];

  // Toggle switch component
  const Toggle = ({ checked, onChange: onToggle, disabled = false }) => (
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      aria-disabled={!!disabled}
      disabled={disabled}
      onClick={() => { if (!disabled) onToggle(!checked); }}
      className={`relative inline-flex h-6 w-10 flex-shrink-0 items-center rounded-full transition-colors ${checked ? 'bg-[#6a1fbf]' : 'bg-white/20'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${checked ? 'translate-x-5' : 'translate-x-1'}`} />
    </button>
  );

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-white/10">
        <h3 className="font-semibold text-white flex items-center gap-2">
          <Palette className="w-4 h-4 text-[#6a1fbf]" />
          Branding
        </h3>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-white/10">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2 px-3 text-xs font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-white border-b-2 border-[#6a1fbf]'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Colors Tab */}
        {activeTab === 'colors' && (
          <>
            {/* Banner Styles */}
            <div>
              <label className="text-gray-400 text-xs mb-2 block">Banner Styles</label>
              <div className="grid grid-cols-2 gap-2">
                {bannerStyles.map((style) => (
                  <button
                    key={style.id}
                    onClick={() => {
                      setPrimaryColor(style.colors.primary);
                      setAccentColor(style.colors.accent);
                      setBackgroundColor(style.colors.background);
                      onChange?.(buildBrandingObject({
                        primaryColor: style.colors.primary,
                        accentColor: style.colors.accent,
                        backgroundColor: style.colors.background,
                      }));
                    }}
                    className={`p-3 rounded-xl transition-all hover:scale-105 ${
                      primaryColor === style.colors.primary && accentColor === style.colors.accent
                        ? 'ring-2 ring-white' : ''
                    }`}
                    style={{
                      background: `linear-gradient(135deg, ${style.colors.primary}, ${style.colors.accent})`
                    }}
                  >
                    <span className="text-xs text-white font-medium">{style.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Colors */}
            <div className="space-y-3">
              {[
                { label: 'Primary Color', value: primaryColor, setter: setPrimaryColor, key: 'primaryColor' },
                { label: 'Accent Color', value: accentColor, setter: setAccentColor, key: 'accentColor' },
                { label: 'Background', value: backgroundColor, setter: setBackgroundColor, key: 'backgroundColor' },
              ].map(({ label, value, setter, key }) => (
                <div key={key}>
                  <label className="text-gray-400 text-xs mb-2 block">{label}</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={value}
                      onChange={(e) => {
                        setter(e.target.value);
                        handleChange(key, e.target.value);
                      }}
                      className="w-10 h-10 rounded-lg cursor-pointer color-picker-dark"
                    />
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => {
                        setter(e.target.value);
                        handleChange(key, e.target.value);
                      }}
                      className="flex-1 bg-white/5 border border-white/10 text-white uppercase px-3 py-2 rounded-lg text-sm"
                    />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Style Tab */}
        {activeTab === 'style' && (
          <>
            {/* Typography */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Type className="w-4 h-4 text-[#6a1fbf]" />
                <label className="text-white text-sm font-semibold">Typography</label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {fonts.map((font) => (
                  <button
                    key={font}
                    onClick={() => {
                      setSelectedFont(font);
                      handleChange('font', font);
                    }}
                    className={`p-2 rounded-lg text-xs transition-all border ${
                      selectedFont === font
                        ? 'bg-[#6a1fbf] text-white border-[#6a1fbf]'
                        : 'bg-white/5 text-gray-300 hover:bg-white/10 border-white/10'
                    }`}
                    style={{ fontFamily: font }}
                  >
                    {font}
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t border-white/10 my-4" />

            {/* Video Frame */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Square className="w-4 h-4 text-[#6a1fbf]" />
                <label className="text-white text-sm font-semibold">Video Frame</label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {frameStyles.map((frame) => (
                  <button
                    key={frame.id}
                    onClick={() => {
                      setSelectedFrame(frame.id);
                      handleChange('frame', frame.id);
                    }}
                    className={`p-2 rounded-lg text-xs transition-all border ${
                      selectedFrame === frame.id
                        ? 'bg-[#6a1fbf] text-white border-[#6a1fbf]'
                        : 'bg-white/5 text-gray-300 hover:bg-white/10 border-white/10'
                    }`}
                  >
                    {frame.name}
                  </button>
                ))}
              </div>

              {selectedFrame !== 'none' && (
                <div className="mt-3 p-3 bg-white/5 rounded-lg border border-white/10 space-y-3">
                  {selectedFrame === 'gradient' ? (
                    <>
                      <div>
                        <label className="text-gray-400 text-xs mb-2 block">Gradient Start</label>
                        <div className="flex gap-2">
                          <input type="color" value={gradientColor1}
                            onChange={(e) => { setGradientColor1(e.target.value); handleChange('gradientColor1', e.target.value); }}
                            className="w-10 h-10 rounded-lg cursor-pointer color-picker-dark" />
                          <input type="text" value={gradientColor1}
                            onChange={(e) => { setGradientColor1(e.target.value); handleChange('gradientColor1', e.target.value); }}
                            className="flex-1 bg-white/5 border border-white/10 text-white uppercase px-3 py-2 rounded-lg text-sm" />
                        </div>
                      </div>
                      <div>
                        <label className="text-gray-400 text-xs mb-2 block">Gradient End</label>
                        <div className="flex gap-2">
                          <input type="color" value={gradientColor2}
                            onChange={(e) => { setGradientColor2(e.target.value); handleChange('gradientColor2', e.target.value); }}
                            className="w-10 h-10 rounded-lg cursor-pointer color-picker-dark" />
                          <input type="text" value={gradientColor2}
                            onChange={(e) => { setGradientColor2(e.target.value); handleChange('gradientColor2', e.target.value); }}
                            className="flex-1 bg-white/5 border border-white/10 text-white uppercase px-3 py-2 rounded-lg text-sm" />
                        </div>
                      </div>
                      <div className="h-3 rounded-lg" style={{ background: `linear-gradient(135deg, ${gradientColor1}, ${gradientColor2})` }} />
                    </>
                  ) : (
                    <div>
                      <label className="text-gray-400 text-xs mb-2 block">Frame Color</label>
                      <div className="flex gap-2">
                        <input type="color" value={frameColor}
                          onChange={(e) => { setFrameColor(e.target.value); handleChange('frameColor', e.target.value); }}
                          className="w-10 h-10 rounded-lg cursor-pointer color-picker-dark" />
                        <input type="text" value={frameColor}
                          onChange={(e) => { setFrameColor(e.target.value); handleChange('frameColor', e.target.value); }}
                          className="flex-1 bg-white/5 border border-white/10 text-white uppercase px-3 py-2 rounded-lg text-sm" />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="text-gray-400 text-xs mb-2 block">Border Width: {frameBorderWidth}px</label>
                    <input type="range" min="1" max="10" value={frameBorderWidth}
                      onChange={(e) => { const v = parseInt(e.target.value); setFrameBorderWidth(v); handleChange('frameBorderWidth', v); }}
                      className="w-full accent-[#6a1fbf]" />
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-300">Frame Shadow</span>
                    <Toggle checked={frameShadow} onChange={(v) => { setFrameShadow(v); handleChange('frameShadow', v); }} />
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-white/10 my-4" />

            {/* Lower Third */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-[#6a1fbf]" />
                <label className="text-white text-sm font-semibold">Lower Third Name Tags</label>
              </div>

              <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl mb-3">
                <span className="text-sm text-gray-300">Show Lower Third</span>
                <Toggle checked={showLowerThird} onChange={(v) => { setShowLowerThird(v); handleChange('showLowerThird', v); }} />
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">Name</label>
                  <input type="text" placeholder="John Smith" value={lowerThirdName}
                    onChange={(e) => { setLowerThirdName(e.target.value); handleChange('lowerThirdName', e.target.value); }}
                    className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2 rounded-lg" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">Title / Role</label>
                  <input type="text" placeholder="CEO, Acme Corp" value={lowerThirdTitle}
                    onChange={(e) => { setLowerThirdTitle(e.target.value); handleChange('lowerThirdTitle', e.target.value); }}
                    className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2 rounded-lg" />
                </div>
              </div>

              <div className="mt-3">
                <label className="text-gray-400 text-xs mb-2 block">Template Style</label>
                <div className="grid grid-cols-2 gap-2">
                  {lowerThirdTemplates.map((tmpl) => (
                    <button
                      key={tmpl.id}
                      onClick={() => { setLowerThirdTemplate(tmpl.id); handleChange('lowerThirdTemplate', tmpl.id); }}
                      className={`p-2 rounded-lg text-left transition-all border ${
                        lowerThirdTemplate === tmpl.id
                          ? 'bg-[#6a1fbf] text-white border-[#6a1fbf]'
                          : 'bg-white/5 text-gray-300 hover:bg-white/10 border-white/10'
                      }`}
                    >
                      <div className="text-xs font-medium">{tmpl.name}</div>
                      <div className="text-[9px] text-gray-400">{tmpl.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Visual Elements */}
              <div className="mt-3 p-3 bg-white/5 rounded-lg border border-white/10 space-y-3">
                <label className="text-white text-xs font-semibold">Visual Elements</label>

                <div>
                  <label className="text-gray-400 text-[10px] mb-2 block">Name Color</label>
                  <div className="flex gap-2">
                    <input type="color" value={lowerThirdTextColor}
                      onChange={(e) => { setLowerThirdTextColor(e.target.value); handleChange('lowerThirdTextColor', e.target.value); }}
                      className="w-10 h-10 rounded-lg cursor-pointer color-picker-dark" />
                    <input type="text" value={lowerThirdTextColor}
                      onChange={(e) => { setLowerThirdTextColor(e.target.value); handleChange('lowerThirdTextColor', e.target.value); }}
                      className="flex-1 bg-white/5 border border-white/10 text-white uppercase px-3 py-2 rounded-lg text-sm" />
                  </div>
                </div>

                <div>
                  <label className="text-gray-400 text-[10px] mb-2 block">Title Color</label>
                  <div className="flex gap-2">
                    <input type="color" value={lowerThirdTitleColor}
                      onChange={(e) => { setLowerThirdTitleColor(e.target.value); handleChange('lowerThirdTitleColor', e.target.value); }}
                      className="w-10 h-10 rounded-lg cursor-pointer color-picker-dark" />
                    <input type="text" value={lowerThirdTitleColor}
                      onChange={(e) => { setLowerThirdTitleColor(e.target.value); handleChange('lowerThirdTitleColor', e.target.value); }}
                      className="flex-1 bg-white/5 border border-white/10 text-white uppercase px-3 py-2 rounded-lg text-sm" />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-300">Show Background</span>
                  <Toggle checked={lowerThirdBackground} onChange={(v) => { setLowerThirdBackground(v); handleChange('lowerThirdBackground', v); }} />
                </div>

                {lowerThirdBackground && (
                  <div>
                    <label className="text-gray-400 text-[10px] mb-2 block">Background Color</label>
                    <div className="flex gap-2">
                      <input type="color" value={lowerThirdBgColor}
                        onChange={(e) => { setLowerThirdBgColor(e.target.value); handleChange('lowerThirdBgColor', e.target.value); }}
                        className="w-10 h-10 rounded-lg cursor-pointer color-picker-dark" />
                      <input type="text" value={lowerThirdBgColor}
                        onChange={(e) => { setLowerThirdBgColor(e.target.value); handleChange('lowerThirdBgColor', e.target.value); }}
                        className="flex-1 bg-white/5 border border-white/10 text-white uppercase px-3 py-2 rounded-lg text-sm" />
                    </div>
                  </div>
                )}

                {/* Underline Toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-300">Underline</span>
                  <Toggle checked={lowerThirdUnderline} onChange={(v) => { setLowerThirdUnderline(v); handleChange('lowerThirdUnderline', v); }} />
                </div>

                {/* Border Toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-300">Border</span>
                  <Toggle checked={lowerThirdBorder} onChange={(v) => { setLowerThirdBorder(v); handleChange('lowerThirdBorder', v); }} />
                </div>

                {lowerThirdBorder && (
                  <div>
                    <label className="text-gray-400 text-[10px] mb-2 block">Border Color</label>
                    <div className="flex gap-2">
                      <input type="color" value={lowerThirdBorderColor}
                        onChange={(e) => { setLowerThirdBorderColor(e.target.value); handleChange('lowerThirdBorderColor', e.target.value); }}
                        className="w-10 h-10 rounded-lg cursor-pointer color-picker-dark" />
                      <input type="text" value={lowerThirdBorderColor}
                        onChange={(e) => { setLowerThirdBorderColor(e.target.value); handleChange('lowerThirdBorderColor', e.target.value); }}
                        className="flex-1 bg-white/5 border border-white/10 text-white uppercase px-3 py-2 rounded-lg text-sm" />
                    </div>
                  </div>
                )}

                {/* Background Shape */}
                <div>
                  <label className="text-gray-400 text-[10px] mb-1 block">Background Shape</label>
                  <div className="grid grid-cols-4 gap-2">
                    {lowerThirdShapes.map((shape) => (
                      <button
                        key={shape.id}
                        onClick={() => { setLowerThirdShape(shape.id); handleChange('lowerThirdShape', shape.id); }}
                        className={`p-2.5 transition-all flex flex-col items-center justify-center gap-1.5 bg-[#2a2d3a] hover:bg-[#32364a] ${
                          lowerThirdShape === shape.id
                            ? 'ring-2 ring-[#6a1fbf]'
                            : 'ring-1 ring-white/10'
                        }`}
                        style={{ borderRadius: '8px' }}
                      >
                        <div
                          className={`w-full h-5 ${lowerThirdShape === shape.id ? 'bg-[#6a1fbf]' : 'bg-[#4a4d5e]'}`}
                          style={{ borderRadius: shape.borderRadius }}
                        />
                        <span className={`text-[8px] font-medium ${lowerThirdShape === shape.id ? 'text-white' : 'text-gray-400'}`}>
                          {shape.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Animations */}
              <div className="mt-3 p-3 bg-white/5 rounded-lg border border-white/10 space-y-2">
                <label className="text-white text-xs font-semibold">Animations</label>
                <div className="grid grid-cols-2 gap-2">
                  {lowerThirdAnimations.map((anim) => (
                    <button
                      key={anim.id}
                      onClick={() => { setLowerThirdAnimation(anim.id); handleChange('lowerThirdAnimation', anim.id); }}
                      className={`p-2 rounded-lg text-xs transition-all border ${
                        lowerThirdAnimation === anim.id
                          ? 'bg-[#6a1fbf] text-white border-[#6a1fbf]'
                          : 'bg-white/5 text-gray-300 hover:bg-white/10 border-white/10'
                      }`}
                    >
                      {anim.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Live Preview */}
              <div className="mt-3 bg-black/40 rounded-xl p-4">
                <label className="text-gray-500 text-[10px] mb-3 block uppercase">Live Preview</label>
                <LowerThirdPreview
                  template={lowerThirdTemplate}
                  name={lowerThirdName || 'Speaker Name'}
                  title={lowerThirdTitle || 'Title / Company'}
                  primaryColor={primaryColor}
                  showBackground={lowerThirdBackground}
                  backgroundColor={lowerThirdBgColor}
                  textColor={lowerThirdTextColor}
                  titleColor={lowerThirdTitleColor}
                  font={selectedFont}
                  underline={lowerThirdUnderline}
                  border={lowerThirdBorder}
                  borderColor={lowerThirdBorderColor}
                  shape={lowerThirdShape}
                  animation={lowerThirdAnimation}
                />
              </div>
            </div>
          </>
        )}

        {/* Media Tab */}
        {activeTab === 'media' && (
          <>
            {/* Logo */}
            <div>
              <label className="text-gray-400 text-sm mb-2 block flex items-center gap-2">
                <Image className="w-4 h-4" />
                Logo
              </label>
              {logoUrl ? (
                <div className="space-y-3">
                  <div className="relative bg-white/5 rounded-xl p-4 flex items-center justify-center">
                    <img src={logoUrl} alt="Logo" className="max-h-16 object-contain" />
                    <button
                      onClick={() => { setLogoUrl(''); setShowLogo(false); handleChange('logoUrl', ''); handleChange('showLogo', false); }}
                      className="absolute top-2 right-2 p-1 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30"
                    >
                      ×
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                    <span className="text-sm text-gray-300">Show Logo</span>
                    <Toggle checked={showLogo} onChange={(v) => { setShowLogo(v); handleChange('showLogo', v); }} />
                  </div>

                  {showLogo && (
                    <div>
                      <label className="text-gray-400 text-xs mb-2 block">Logo Position</label>
                      <div className="grid grid-cols-2 gap-1">
                        {['top-left', 'top-right'].map(pos => (
                          <button
                            key={pos}
                            onClick={() => { setLogoPosition(pos); handleChange('logoPosition', pos); }}
                            className={`p-2 rounded-lg text-xs capitalize ${
                              logoPosition === pos ? 'bg-[#6a1fbf] text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
                            }`}
                          >
                            {pos.replace('-', ' ')}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <label className="block cursor-pointer">
                    <div className="flex items-center justify-center w-full h-24 border-2 border-dashed border-white/20 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                      {isUploadingLogo ? (
                        <div className="flex items-center gap-2 text-gray-400">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span className="text-sm">Uploading...</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-1 text-gray-400">
                          <Upload className="w-6 h-6" />
                          <span className="text-xs">Click to upload logo</span>
                        </div>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      disabled={isUploadingLogo}
                    />
                  </label>
                  <p className="text-[10px] text-gray-500 mt-2 text-center">Or enter URL below</p>
                  <input
                    type="text"
                    placeholder="https://example.com/logo.png"
                    value={logoUrl}
                    onChange={(e) => { setLogoUrl(e.target.value); handleChange('logoUrl', e.target.value); }}
                    className="mt-2 w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2 rounded-lg"
                  />
                </div>
              )}
            </div>

            {/* AI Background Generator */}
            <div>
              <label className="text-gray-400 text-sm mb-2 block flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                AI Background Generator
              </label>
              <p className="text-xs text-gray-500 mb-2">Describe a background and AI will create it for you</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') generateAiBackground(); }}
                  placeholder="e.g., Modern office with city view..."
                  className="flex-1 bg-white/5 border border-white/10 text-white text-sm px-3 py-2 rounded-lg"
                />
                <button
                  onClick={generateAiBackground}
                  disabled={isGenerating || !aiPrompt.trim()}
                  className="px-3 py-2 rounded-lg text-white disabled:opacity-50"
                  style={{ background: 'linear-gradient(to right, #6a1fbf, #00c853)' }}
                >
                  {isGenerating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Wand2 className="w-4 h-4" />
                  )}
                </button>
              </div>
              {generatedBgUrl && (
                <div className="mt-3 space-y-2">
                  <div
                    className={`relative cursor-pointer rounded-xl overflow-hidden border-2 transition-colors ${
                      activeBackground === generatedBgUrl ? 'border-[#6a1fbf]' : 'border-transparent hover:border-white/30'
                    }`}
                    onClick={() => {
                      if (activeBackground === generatedBgUrl) {
                        setActiveBackground('');
                        handleChange('activeBackground', '');
                      } else {
                        setActiveBackground(generatedBgUrl);
                        handleChange('activeBackground', generatedBgUrl);
                      }
                    }}
                  >
                    <img
                      src={generatedBgUrl}
                      alt="Generated Background"
                      className="w-full h-32 object-cover"
                    />
                    {activeBackground === generatedBgUrl && (
                      <div className="absolute inset-0 bg-[#6a1fbf]/20 flex items-center justify-center">
                        <Check className="w-8 h-8 text-white" />
                      </div>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (activeBackground === generatedBgUrl) {
                          handleChange('activeBackground', '');
                        }
                        setGeneratedBgUrl('');
                        setActiveBackground('');
                      }}
                      className="absolute top-2 right-2 p-1 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30"
                    >
                      ×
                    </button>
                  </div>
                  <p className="text-[10px] text-gray-500 text-center">
                    {activeBackground === generatedBgUrl ? 'Background active - click to remove' : 'Click to set as background'}
                  </p>
                </div>
              )}
            </div>
          </>
        )}

        {/* QR Code Tab */}
        {activeTab === 'qr' && (
          <div className="space-y-4">
            <div>
              <label className="text-gray-400 text-sm mb-2 block flex items-center gap-2">
                <QrCode className="w-4 h-4" />
                QR Code Generator
              </label>
              <p className="text-xs text-gray-500 mb-3">Create a branded QR code overlay for your stream</p>
              <input
                type="text"
                value={qrUrl}
                onChange={(e) => { setQrUrl(e.target.value); handleChange('qrUrl', e.target.value); }}
                placeholder="https://example.com"
                className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2 rounded-lg"
              />
            </div>

            {qrUrl && (
              <div className="space-y-4">
                {/* QR Code Preview */}
                <div className="rounded-xl p-4 w-40 h-40 mx-auto shadow-lg relative" style={{ backgroundColor: qrBgColor }}>
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&color=${qrColor.replace('#', '')}&bgcolor=${qrBgColor.replace('#', '')}&data=${encodeURIComponent(qrUrl)}`}
                    alt="QR Code"
                    className="w-full h-full object-contain"
                  />
                  {qrLogo && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: qrBgColor }}>
                        <img src={qrLogo} alt="Logo" className="w-8 h-8 object-contain rounded-full" />
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-[10px] text-gray-500 text-center truncate px-2">{qrUrl}</p>

                {/* Colors */}
                <div className="space-y-3">
                  <label className="text-gray-400 text-xs">QR Code Colors</label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-gray-500 text-[10px] mb-1 block">Code Color</label>
                      <div className="flex gap-2">
                        <input type="color" value={qrColor}
                          onChange={(e) => { setQrColor(e.target.value); handleChange('qrColor', e.target.value); }}
                          className="w-8 h-8 rounded cursor-pointer color-picker-dark" />
                        <input type="text" value={qrColor}
                          onChange={(e) => { setQrColor(e.target.value); handleChange('qrColor', e.target.value); }}
                          className="flex-1 min-w-0 bg-white/5 border border-white/10 text-white uppercase px-2 py-1.5 rounded-lg text-xs" />
                      </div>
                    </div>
                    <div>
                      <label className="text-gray-500 text-[10px] mb-1 block">Background</label>
                      <div className="flex gap-2">
                        <input type="color" value={qrBgColor}
                          onChange={(e) => { setQrBgColor(e.target.value); handleChange('qrBgColor', e.target.value); }}
                          className="w-8 h-8 rounded cursor-pointer color-picker-dark" />
                        <input type="text" value={qrBgColor}
                          onChange={(e) => { setQrBgColor(e.target.value); handleChange('qrBgColor', e.target.value); }}
                          className="flex-1 min-w-0 bg-white/5 border border-white/10 text-white uppercase px-2 py-1.5 rounded-lg text-xs" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Center Logo */}
                <div>
                  <label className="text-gray-400 text-xs mb-2 block">Center Logo (optional)</label>
                  {qrLogo ? (
                    <div className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
                      <img src={qrLogo} alt="QR Logo" className="w-10 h-10 rounded-lg object-contain bg-white p-1" />
                      <span className="text-xs text-gray-400 flex-1">Logo added</span>
                      <button
                        onClick={() => { setQrLogo(''); handleChange('qrLogo', ''); }}
                        className="p-1 text-red-400 hover:bg-red-500/20 rounded"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <label className="block">
                      <div className="flex items-center justify-center w-full h-16 border-2 border-dashed border-white/20 rounded-xl bg-white/5 hover:bg-white/10 cursor-pointer transition-colors">
                        {isUploadingQrLogo ? (
                          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                        ) : (
                          <div className="flex items-center gap-2 text-gray-400">
                            <Upload className="w-4 h-4" />
                            <span className="text-xs">Upload center logo</span>
                          </div>
                        )}
                      </div>
                      <input
                        ref={qrLogoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleQrLogoUpload}
                        className="hidden"
                        disabled={isUploadingQrLogo}
                      />
                    </label>
                  )}
                </div>

                {/* Size */}
                <div>
                  <label className="text-gray-400 text-xs mb-2 block">Size</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['small', 'medium', 'large'].map(size => (
                      <button
                        key={size}
                        onClick={() => { setQrSize(size); handleChange('qrSize', size); }}
                        className={`p-2 rounded-xl text-xs capitalize ${
                          qrSize === size ? 'bg-[#6a1fbf] text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Position — only meaningful with a URL set */}
                {showQrCode && (
                  <div>
                    <label className="text-gray-400 text-xs mb-2 block">Position on Screen</label>
                    <div className="grid grid-cols-3 gap-2">
                      {['top-left', 'top-center', 'top-right', 'middle-left', 'middle-right', 'bottom-left', 'bottom-center', 'bottom-right'].map(pos => (
                        <button
                          key={pos}
                          onClick={() => { setQrPosition(pos); handleChange('qrPosition', pos); }}
                          className={`p-2 rounded-xl text-[10px] capitalize ${
                            qrPosition === pos ? 'bg-[#6a1fbf] text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
                          }`}
                        >
                          {pos.replace('-', ' ')}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Show on Stream toggle — kept outside the qrUrl gate so the host
                always has explicit control over visibility, even after
                clearing the URL.  Without this, clicking "Clear QR Code"
                collapsed the entire QR settings block (since it sits inside
                the `{qrUrl && …}` branch above) and the toggle vanished, so
                the host couldn't confirm broadcast had stopped. */}
            <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
              <span className="text-sm text-gray-300">Show on Stream</span>
              <Toggle
                checked={showQrCode}
                disabled={!qrUrl}
                onChange={(v) => { setShowQrCode(v); handleChange('showQrCode', v); }}
              />
            </div>

            {/* Clear — only useful when there's something to clear */}
            {qrUrl && (
              <button
                onClick={() => {
                  setQrUrl(''); setShowQrCode(false); setQrLogo('');
                  // Single onChange with all three overrides — calling
                  // handleChange three times runs buildBrandingObject against
                  // stale React state each time, so the last call would
                  // resurrect showQrCode=true and the old qrUrl, leaving the
                  // QR visible on every participant's screen.
                  onChange?.(buildBrandingObject({ qrUrl: '', showQrCode: false, qrLogo: '' }));
                }}
                className="w-full p-2 text-sm text-red-400 bg-red-500/10 hover:bg-red-500/20 rounded-xl"
              >
                Clear QR Code
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function LowerThirdPreview({
  template, name, title, primaryColor, showBackground, backgroundColor, textColor, titleColor, font,
  underline, border, borderColor, shape, animation
}) {
  // Apply shape styling based on selected shape
  const shapeStyles = {
    bubble: { borderRadius: '999px', padding: '8px 16px' },
    classic: { borderRadius: '12px', padding: '8px 12px' },
    minimal: { borderRadius: '4px', padding: '6px 10px' },
    block: { borderRadius: '0px', padding: '10px 14px' }
  };

  const selectedShape = shapeStyles[shape] || shapeStyles.minimal;

  const containerStyle = {
    fontFamily: font,
    borderRadius: selectedShape.borderRadius,
    padding: selectedShape.padding,
    ...(showBackground && { backgroundColor: `${backgroundColor}b3` }),
    ...(border && { border: `2px solid ${borderColor}` }),
    ...(underline && { borderBottom: `3px solid ${primaryColor}`, paddingBottom: '8px' }),
  };

  // CSS animation classes
  const animationStyles = {
    'none': {},
    'fade': { animation: 'fadeIn 0.5s ease-out' },
    'slide-left': { animation: 'slideInFromRight 0.5s ease-out' },
    'slide-right': { animation: 'slideInFromLeft 0.5s ease-out' },
    'slide-up': { animation: 'slideInFromBottom 0.5s ease-out' },
    'bounce': { animation: 'bounceIn 0.5s ease-out' }
  };

  const templates = {
    minimal: (
      <div style={{ ...containerStyle, ...animationStyles[animation] }}>
        <div className="flex items-center">
          <div className="w-1 h-6 mr-2" style={{ backgroundColor: primaryColor }} />
          <div className="flex items-baseline gap-2">
            {title && <span className="text-[10px] uppercase tracking-wide" style={{ color: titleColor }}>{title}</span>}
            <span className="font-bold text-sm" style={{ color: textColor }}>{name}</span>
          </div>
        </div>
      </div>
    ),
    modern: (
      <div style={{ ...containerStyle, ...animationStyles[animation] }}>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2" style={{ backgroundColor: primaryColor }} />
          <span className="font-semibold text-sm" style={{ color: textColor }}>{name}</span>
          {title && <span className="text-xs italic" style={{ color: titleColor }}>{title}</span>}
        </div>
      </div>
    ),
    classic: (
      <div style={{ fontFamily: font, ...animationStyles[animation] }}>
        <div className="h-1 w-full" style={{ backgroundColor: primaryColor }} />
        <div className="px-3 py-1.5 flex items-center gap-3" style={{
          backgroundColor: showBackground ? backgroundColor : '#2a2a2a',
          ...(border ? { border: `2px solid ${borderColor}` } : {})
        }}>
          <span className="font-bold text-sm" style={{ color: textColor }}>{name}</span>
          {title && <span className="text-xs" style={{ color: titleColor }}>{title}</span>}
        </div>
      </div>
    ),
    bold: (
      <div style={{ ...containerStyle, ...animationStyles[animation] }}>
        <div className="flex items-center">
          <div className="w-0.5 h-5 mr-2" style={{ backgroundColor: primaryColor }} />
          {title && <span className="text-[9px] uppercase tracking-wider mr-2" style={{ color: titleColor }}>{title}</span>}
          <span className="font-bold text-sm" style={{ color: textColor }}>{name}</span>
        </div>
      </div>
    ),
    broadcast: (
      <div style={{ ...containerStyle, ...animationStyles[animation] }}>
        <div className="flex items-center">
          <div className="h-px w-3 mr-1" style={{ backgroundColor: primaryColor }} />
          <div className="border-l-2 border-r-2 px-3 py-1 flex items-center gap-2" style={{ borderColor: primaryColor }}>
            <span className="font-bold text-sm" style={{ color: textColor }}>{name}</span>
            {title && <span className="text-[10px] uppercase tracking-wider" style={{ color: titleColor }}>{title}</span>}
          </div>
          <div className="h-px w-3 ml-1" style={{ backgroundColor: primaryColor }} />
        </div>
      </div>
    ),
    elegant: (
      <div style={{ ...containerStyle, ...animationStyles[animation] }}>
        <div className="flex items-center">
          <div className="flex flex-col mr-2">
            <div className="w-1 h-4" style={{ backgroundColor: primaryColor }} />
            <div className="w-3 h-1" style={{ backgroundColor: primaryColor }} />
          </div>
          {title && <span className="text-[9px] uppercase tracking-wider mr-2" style={{ color: primaryColor }}>{title}</span>}
          <span className="font-bold text-sm" style={{ color: textColor }}>{name}</span>
        </div>
      </div>
    ),
    corporate: (
      <div style={{ ...containerStyle, ...animationStyles[animation] }}>
        <div className="flex items-center">
          <div className="w-1 h-5 mr-2" style={{ backgroundColor: primaryColor }} />
          <span className="font-bold text-sm" style={{ color: textColor }}>{name}</span>
          {title && <span className="text-[10px] uppercase tracking-wider ml-2" style={{ color: titleColor }}>{title}</span>}
        </div>
      </div>
    ),
    creative: (
      <div style={{ ...containerStyle, ...animationStyles[animation] }}>
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm" style={{ color: textColor }}>{name}</span>
          {title && <span className="text-xs" style={{ color: titleColor }}>{title}</span>}
        </div>
      </div>
    ),
  };

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideInFromRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideInFromLeft {
          from { transform: translateX(-100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideInFromBottom {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes bounceIn {
          0% { transform: scale(0.3); opacity: 0; }
          50% { transform: scale(1.05); }
          70% { transform: scale(0.9); }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
      {templates[template] || templates.modern}
    </>
  );
}
